import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Cake, Heart } from "lucide-react";
import { useOrg } from "../org";
import { dayLabel } from "./text";

const KIND = { birthday: "Birthday", anniversary: "Wedding anniversary" };
const when = (n) => (n === 0 ? "Today" : n === 1 ? "Tomorrow" : `In ${n} days`);
const monthDay = (iso) => new Date(Date.UTC(2000, +iso.slice(5, 7) - 1, +iso.slice(8, 10))).toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });

export const TodayMoments = () => {
  const { api } = useOrg();
  const [days, setDays] = useState(14);
  const [items, setItems] = useState(null);
  useEffect(() => { api(`/life-moments?days=${days}`).then(setItems).catch(() => setItems([])); }, [api, days]);
  if (!items || (!items.length && days === 14)) return null;
  return (
    <section className="mt-14" data-testid="today-life-moments">
      <div className="flex items-end justify-between gap-3">
        <div><h2 className="t-heading">Life Moments</h2><p className="t-meta mt-1">Birthdays and anniversaries in the next {days} days</p></div>
        <button onClick={() => setDays(days === 14 ? 30 : 14)} className="btn btn-quiet btn-sm shrink-0" data-testid="life-moments-range-btn">{days === 14 ? "Next 30 days" : "Next 14 days"}</button>
      </div>
      <div className="rule-list border-y border-line mt-4">
        {items.map((m) => {
          const Icon = m.kind === "birthday" ? Cake : Heart;
          return (
            <Link key={m.key} to={`/people/${m.person_id}`} className="flex items-center gap-4 py-4 group" data-testid={`life-moment-${m.key}`}>
              <span className="h-11 w-11 rounded-full bg-sunk text-brand inline-flex items-center justify-center shrink-0"><Icon size={18} /></span>
              <div className="flex-1 min-w-0"><p className="t-name-sm group-hover:text-brand transition-colors [overflow-wrap:anywhere]">{m.name}</p><p className="t-meta">{KIND[m.kind]} · {dayLabel(m.date)}</p></div>
              <span className={`text-sm font-medium shrink-0 ${m.days <= 1 ? "text-brand" : "text-ink-soft"}`} data-testid={`life-moment-days-${m.key}`}>{when(m.days)}</span>
            </Link>
          );
        })}
        {!items.length && <p className="t-meta py-4" data-testid="life-moments-empty">No birthdays or anniversaries in the next {days} days.</p>}
      </div>
    </section>
  );
};

const DateField = ({ label, value, set, locked, testid }) => (
  <label className="block mt-3">
    <span className="t-meta">{label}{locked ? " · from Planning Center" : ""}</span>
    <input type="date" value={value || ""} disabled={locked} onChange={(e) => set(e.target.value || null)} className="field h-12 px-3 w-full mt-1 disabled:opacity-60" data-testid={testid} />
  </label>
);

const EditMoments = ({ p, onClose, onSaved }) => {
  const { api } = useOrg();
  const locked = p.pco_date_fields || [];
  const [birth, setBirth] = useState(p.birth_date);
  const [anniv, setAnniv] = useState(p.anniversary_date);
  const [spouse, setSpouse] = useState(p.spouse?.id || "");
  const [busy, setBusy] = useState(false);
  const options = [...(p.spouse ? [p.spouse] : []), ...(p.household_members || []).filter((m) => m.id !== p.spouse?.id).map((m) => ({ id: m.id, name: `${m.preferred_name || m.first_name} ${m.last_name}` }))];
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (!locked.includes("birth_date") && birth !== p.birth_date) body.birth_date = birth;
      if (!locked.includes("anniversary_date") && anniv !== p.anniversary_date) body.anniversary_date = anniv;
      if (Object.keys(body).length) await api(`/people/${p.id}`, { method: "PATCH", body });
      if (spouse !== (p.spouse?.id || "")) await api(`/people/${p.id}/spouse`, { method: "PUT", body: { spouse_id: spouse || null } });
      toast.success("Life Moments saved");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="life-moments-form">
      <DateField label="Birthday" value={birth} set={setBirth} locked={locked.includes("birth_date")} testid="life-moments-birth" />
      <DateField label="Wedding anniversary" value={anniv} set={setAnniv} locked={locked.includes("anniversary_date")} testid="life-moments-anniversary" />
      <label className="block mt-3">
        <span className="t-meta">Spouse (confirmed)</span>
        <select value={spouse} onChange={(e) => setSpouse(e.target.value)} className="field h-12 px-3 w-full mt-1" data-testid="life-moments-spouse">
          <option value="">Not set</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <span className="t-meta block mt-1">Only set this when you know they're married. Sharing a household isn't enough.</span>
      </label>
      <div className="flex gap-2 mt-4">
        <button disabled={busy} onClick={save} className="btn btn-primary btn-sm" data-testid="life-moments-save">Save</button>
        <button onClick={onClose} className="btn btn-quiet btn-sm" data-testid="life-moments-cancel">Cancel</button>
      </div>
    </div>
  );
};

export const PersonMoments = ({ p, c, Section, reload }) => {
  const [edit, setEdit] = useState(false);
  if (!c.directory && !p.birth_date && !p.anniversary_date) return null;
  return (
    <Section title="Life Moments" testid="person-life-moments" action={c.directory && !edit && <button onClick={() => setEdit(true)} className="btn btn-quiet btn-sm" data-testid="life-moments-edit-btn">Edit</button>}>
      {edit ? <EditMoments p={p} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); reload(); }} /> : (
        <div className="space-y-2">
          <p className="t-body flex items-center gap-2" data-testid="person-birthday"><Cake size={16} className="text-brand" />{p.birth_date ? `Birthday · ${monthDay(p.birth_date)}` : <span className="t-meta">No birthday added</span>}</p>
          <p className="t-body flex items-center gap-2" data-testid="person-anniversary"><Heart size={16} className="text-brand" />{p.anniversary_date ? `Anniversary · ${monthDay(p.anniversary_date)}${p.spouse ? ` · with ${p.spouse.name}` : ""}` : <span className="t-meta">No anniversary added</span>}</p>
        </div>
      )}
    </Section>
  );
};
